import { type RefObject, useEffect, useLayoutEffect, useRef } from 'react';

import type { GalleryScopeTarget } from './gallerySections';

type GalleryLandmark = {
  element: HTMLElement;
  target: GalleryScopeTarget;
};

const ACTIVATION_OFFSET = 41;

const getTargetKey = ({ groupId, taskId }: GalleryScopeTarget): string => taskId ? `task:${groupId}:${taskId}` : groupId ? `group:${groupId}` : 'overview';

const getLandmarks = (content: HTMLElement): GalleryLandmark[] => (
  [...content.querySelectorAll<HTMLElement>('[data-gallery-landmark]')].map(element => ({
    element,
    target: {
      groupId: element.dataset.galleryGroupId,
      taskId: element.dataset.galleryTaskId,
    },
  }))
);

const getActiveLandmark = (content: HTMLElement): GalleryLandmark | undefined => {
  const scrollBody = content.parentElement;
  if (!scrollBody) return undefined;
  const activationTop = scrollBody.getBoundingClientRect().top + ACTIVATION_OFFSET;
  return getLandmarks(content).reduce<GalleryLandmark | undefined>(
    (active, landmark) =>
      landmark.element.getBoundingClientRect().top <= activationTop
        ? landmark
        : active,
    undefined,
  );
};

const findLandmark = (
  content: HTMLElement,
  target: GalleryScopeTarget,
): GalleryLandmark | undefined =>
  getLandmarks(content)
    .find(landmark => getTargetKey(landmark.target) === getTargetKey(target));

/** Keeps routed Gallery scope and the active vertical landmark synchronized. */
export const useGalleryScopeNavigation = ({
  selectedTarget,
  landmarkSignature,
  onActiveTargetChange,
}: {
  selectedTarget: GalleryScopeTarget;
  landmarkSignature: string;
  onActiveTargetChange?: (target: GalleryScopeTarget) => void;
}): RefObject<HTMLDivElement | null> => {
  const contentRef = useRef<HTMLDivElement>(null);
  const { groupId, taskId } = selectedTarget;
  const selectedKey = getTargetKey(selectedTarget);
  const reportedKeyRef = useRef(selectedKey);
  const handledSelectedKeyRef = useRef<string | undefined>(undefined);

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    if (getTargetKey(getActiveLandmark(content)?.target ?? {}) === selectedKey) {
      handledSelectedKeyRef.current = selectedKey;
      return;
    }
    if (handledSelectedKeyRef.current === selectedKey) return;
    const landmark = findLandmark(content, { groupId, taskId });
    if (!landmark) return;
    handledSelectedKeyRef.current = selectedKey;
    landmark.element.scrollIntoView?.({ block: 'start' });
  }, [groupId, landmarkSignature, selectedKey, taskId]);

  useEffect(() => {
    reportedKeyRef.current = selectedKey;
  }, [selectedKey]);

  useEffect(() => {
    const content = contentRef.current;
    const scrollBody = content?.parentElement;
    if (!content || !scrollBody || !onActiveTargetChange) return;
    let frame = 0;
    const syncActiveTarget = () => {
      frame = 0;
      const target = getActiveLandmark(content)?.target ?? {};
      const targetKey = getTargetKey(target);
      if (targetKey === selectedKey || targetKey === reportedKeyRef.current) return;
      reportedKeyRef.current = targetKey;
      onActiveTargetChange(target);
    };
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(syncActiveTarget);
    };
    scrollBody.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scrollBody.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [landmarkSignature, onActiveTargetChange, selectedKey]);

  return contentRef;
};
